import { AuthService, AccountService, MemberService, TradeService, StockService, TermsService, WatchListService, PaymentService, ExecutionService, OrderService, QuoteService, AssetService, KcbPhoneVerificationService, NotificationService, OpenApiServiceService, OpenApiKeyService, } from './generated';
import { CommunityQNAService, CommunityNoticeService, CommunityFAQService, CommunityEventService, } from './generated/community';
import { applyGuard } from '../guards';
/**
 * 서비스 이름을 저장하기 위한 Symbol
 */
const SERVICE_NAME_SYMBOL = Symbol.for('@template/api:serviceName');
/**
 * 서비스 인스턴스에 이름을 저장하는 헬퍼 함수
 */
function setServiceName(service, name) {
    service[SERVICE_NAME_SYMBOL] = name;
    return service;
}
/**
 * 모든 API 서비스 인스턴스를 생성하는 팩토리 함수
 *
 * @param options - 서비스 생성 옵션
 * @returns 생성된 API 서비스 인스턴스들
 *
 * @example
 * ```typescript
 * // Guard 없이 생성
 * const { authService, memberService } = createApiServices({
 *   axiosInstance: api
 * });
 *
 * // Guard와 함께 생성
 * const { authService, memberService } = createApiServices({
 *   axiosInstance: api,
 *   guardConfig: {
 *     checkLogin: () => useUserStore().checkLoggedIn(),
 *     checkAccount: () => useAccountStore().hasSelectedAccount,
 *   }
 * });
 *
 * // 커뮤니티 서비스에 별도 Guard 설정
 * const { communityQNAService, communityNoticeService } = createApiServices({
 *   axiosInstance: api,
 *   guardConfig: {
 *     checkLogin: () => useUserStore().checkLoggedIn(),
 *     checkAccount: () => useAccountStore().hasSelectedAccount,
 *   },
 *   communityGuardConfig: {
 *     checkLogin: () => useUserStore().checkLoggedIn(),
 *     checkAccount: () => useAccountStore().hasSelectedAccount,
 *   }
 * });
 * ```
 */
export function createApiServices(options) {
    const { axiosInstance, guardConfig, communityGuardConfig } = options;
    // 서비스 인스턴스 생성 및 이름 저장 (minification 방지)
    const _authService = setServiceName(new AuthService(axiosInstance), 'AuthService');
    const _accountService = setServiceName(new AccountService(axiosInstance), 'AccountService');
    const _memberService = setServiceName(new MemberService(axiosInstance), 'MemberService');
    const _tradeService = setServiceName(new TradeService(axiosInstance), 'TradeService');
    const _executionService = setServiceName(new ExecutionService(axiosInstance), 'ExecutionService');
    const _stockService = setServiceName(new StockService(axiosInstance), 'StockService');
    const _termsService = setServiceName(new TermsService(axiosInstance), 'TermsService');
    const _watchlistService = setServiceName(new WatchListService(axiosInstance), 'WatchListService');
    const _paymentService = setServiceName(new PaymentService(axiosInstance), 'PaymentService');
    const _orderService = setServiceName(new OrderService(axiosInstance), 'OrderService');
    const _quoteService = setServiceName(new QuoteService(axiosInstance), 'QuoteService');
    const _assetService = setServiceName(new AssetService(axiosInstance), 'AssetService');
    const _kcbPhoneVerificationService = setServiceName(new KcbPhoneVerificationService(axiosInstance), 'KcbPhoneVerificationService');
    const _notificationService = setServiceName(new NotificationService(axiosInstance), 'NotificationService');
    const _openApiService = setServiceName(new OpenApiServiceService(axiosInstance), 'OpenApiServiceService');
    const _openApiKeyService = setServiceName(new OpenApiKeyService(axiosInstance), 'OpenApiKeyService');
    const _communityQNAService = setServiceName(new CommunityQNAService(axiosInstance), 'CommunityQNAService');
    const _communityNoticeService = setServiceName(new CommunityNoticeService(axiosInstance), 'CommunityNoticeService');
    const _communityFAQService = setServiceName(new CommunityFAQService(axiosInstance), 'CommunityFAQService');
    const _communityEventService = setServiceName(new CommunityEventService(axiosInstance), 'CommunityEventService');
    const communityGuard = communityGuardConfig ?? guardConfig;
    // Guard 적용 여부에 따라 반환
    // 서비스 이름은 Symbol로 저장되어 있어 applyGuard에서 자동으로 찾을 수 있음
    if (guardConfig) {
        return {
            authService: applyGuard(_authService, guardConfig),
            accountService: applyGuard(_accountService, guardConfig),
            memberService: applyGuard(_memberService, guardConfig),
            tradeService: applyGuard(_tradeService, guardConfig),
            executionService: applyGuard(_executionService, guardConfig),
            stockService: applyGuard(_stockService, guardConfig),
            termsService: applyGuard(_termsService, guardConfig),
            watchlistService: applyGuard(_watchlistService, guardConfig),
            paymentService: applyGuard(_paymentService, guardConfig),
            orderService: applyGuard(_orderService, guardConfig),
            quoteService: applyGuard(_quoteService, guardConfig),
            assetService: applyGuard(_assetService, guardConfig),
            kcbPhoneVerificationService: applyGuard(_kcbPhoneVerificationService, guardConfig),
            notificationService: applyGuard(_notificationService, guardConfig),
            openApiService: applyGuard(_openApiService, guardConfig),
            openApiKeyService: applyGuard(_openApiKeyService, guardConfig),
            communityQNAService: communityGuard
                ? applyGuard(_communityQNAService, communityGuard)
                : _communityQNAService,
            communityNoticeService: communityGuard
                ? applyGuard(_communityNoticeService, communityGuard)
                : _communityNoticeService,
            communityFAQService: communityGuard
                ? applyGuard(_communityFAQService, communityGuard)
                : _communityFAQService,
            communityEventService: communityGuard
                ? applyGuard(_communityEventService, communityGuard)
                : _communityEventService,
        };
    }
    // Guard 없이 반환
    return {
        authService: _authService,
        accountService: _accountService,
        memberService: _memberService,
        tradeService: _tradeService,
        executionService: _executionService,
        stockService: _stockService,
        termsService: _termsService,
        watchlistService: _watchlistService,
        paymentService: _paymentService,
        orderService: _orderService,
        quoteService: _quoteService,
        assetService: _assetService,
        kcbPhoneVerificationService: _kcbPhoneVerificationService,
        notificationService: _notificationService,
        openApiService: _openApiService,
        openApiKeyService: _openApiKeyService,
        communityQNAService: _communityQNAService,
        communityNoticeService: _communityNoticeService,
        communityFAQService: _communityFAQService,
        communityEventService: _communityEventService,
    };
}
//# sourceMappingURL=factory.js.map